/*
 * GLOBAL EVENTS CALENDAR
 * Matrix Core / Shopify
 *
 * Features:
 * - Events from /eventlistclean
 * - Availability from /eventlist
 * - Monthly progressive loading
 * - Prefetch next month
 * - Cached completed and pending requests
 * - Debounced search
 * - UK timezone support
 * - Availability badges
 * - Empty-month messages
 * - Lazy-loaded event images
 * - Shopify Theme Editor lifecycle
 */

class GlobalEventsCalendar {
  constructor(toolbar) {
    this.toolbar = toolbar;
    this.root = toolbar.parentElement;

    // ----------------------------------------
    // CONFIGURATION
    // ----------------------------------------

    this.apiUrl = "/apps/matrix/eventlistclean";
    this.availabilityApiUrl = "/apps/matrix/eventlist";

    this.timeZone = "Europe/London";
    this.monthCount = 12;
    this.prefetchCount = 1;
    this.searchDelay = 200;

    // ----------------------------------------
    // CACHED DATE FORMATTERS
    // ----------------------------------------

    this.ukDateFormatter = new Intl.DateTimeFormat(
      "en-GB",
      {
        timeZone: this.timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23"
      }
    );

    this.monthFormatter = new Intl.DateTimeFormat(
      "en-GB",
      {
        month: "long",
        year: "numeric",
        timeZone: "UTC"
      }
    );

    this.weekdayFormatter = new Intl.DateTimeFormat(
      "en-GB",
      {
        weekday: "short",
        timeZone: this.timeZone
      }
    );

    // ----------------------------------------
    // LIQUID TEMPLATES
    // ----------------------------------------

    this.monthTemplate = this.root.querySelector(
      "[data-event-month-template]"
    );

    this.cardTemplate = this.root.querySelector(
      "[data-event-card-template]"
    );

    this.monthOptionTemplate = this.root.querySelector(
      "[data-event-month-option-template]"
    );

    // ----------------------------------------
    // FILTER ELEMENTS
    // ----------------------------------------

    this.searchInput = toolbar.querySelector(
      "[data-event-search]"
    );

    this.monthInput = toolbar.querySelector(
      "[data-event-month]"
    );

    this.monthDropdown = toolbar.querySelector(
      "[data-event-month-dropdown]"
    );

    this.monthTrigger = toolbar.querySelector(
      "[data-event-month-trigger]"
    );

    this.monthMenu = toolbar.querySelector(
      "[data-event-month-menu]"
    );

    this.monthLabel = toolbar.querySelector(
      "[data-event-month-label]"
    );

    this.clearButton = toolbar.querySelector(
      "[data-event-clear]"
    );

    this.resultsElement = toolbar.querySelector(
      "[data-event-results]"
    );

    // ----------------------------------------
    // RESULTS ELEMENTS
    // ----------------------------------------

    this.calendarContainer = this.root.querySelector(
      "[data-events-calendar-results]"
    );

    this.monthsContainer =
      this.calendarContainer?.querySelector(
        "[data-events-calendar-months]"
      );

    this.loadingElement =
      this.calendarContainer?.querySelector(
        "[data-events-loading]"
      );

    this.emptyElement =
      this.calendarContainer?.querySelector(
        "[data-calendar-empty]"
      );

    // ----------------------------------------
    // STATE
    // ----------------------------------------

    this.events = [];
    this.filteredEvents = [];
    this.months = [];

    this.monthCache = new Map();
    this.pendingRequests = new Map();

    this.abortController = new AbortController();

    this.destroyed = false;
    this.requestId = 0;
    this.prefetchId = 0;
    this.searchTimer = null;

    // ----------------------------------------
    // EVENT HANDLERS
    // ----------------------------------------

    this.handleSearch = this.onSearch.bind(this);
    this.handleClear = this.clearFilters.bind(this);
    this.handleOutsideClick = this.onOutsideClick.bind(this);
    this.handleMonthTrigger =
      this.toggleMonthDropdown.bind(this);
    this.handleMonthMenuClick =
      this.onMonthMenuClick.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);

    this.init();
  }

  // ========================================
  // UK DATE HELPERS
  // ========================================

  getUKParts(date = new Date()) {
    const parts =
      this.ukDateFormatter.formatToParts(date);

    return Object.fromEntries(
      parts
        .filter(part => part.type !== "literal")
        .map(part => [
          part.type,
          Number(part.value)
        ])
    );
  }

  getCurrentMonthKey() {
    const now = this.getUKParts();

    return (
      `${now.year}-` +
      String(now.month).padStart(2, "0")
    );
  }

  getUKTimestamp(year, month, day) {
    const target = Date.UTC(
      year,
      month - 1,
      day
    );

    let timestamp = target;

    for (let i = 0; i < 4; i++) {
      const parts = this.getUKParts(
        new Date(timestamp)
      );

      const represented = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second
      );

      const difference = target - represented;

      if (difference === 0) break;

      timestamp += difference;
    }

    return Math.floor(timestamp / 1000);
  }

  getMonthRange(monthKey) {
    const [year, month] = monthKey
      .split("-")
      .map(Number);

    const startDate = this.getUKTimestamp(
      year,
      month,
      1
    );

    const nextMonth = new Date(
      Date.UTC(year, month, 1)
    );

    const finishDate = this.getUKTimestamp(
      nextMonth.getUTCFullYear(),
      nextMonth.getUTCMonth() + 1,
      1
    ) - 1;

    return {
      startDate,
      finishDate
    };
  }

  buildMonths() {
    const now = this.getUKParts();

    this.months = [];

    for (let i = 0; i < this.monthCount; i++) {
      const date = new Date(
        Date.UTC(
          now.year,
          now.month - 1 + i,
          1
        )
      );

      const year = date.getUTCFullYear();
      const month = date.getUTCMonth() + 1;

      const value =
        `${year}-` +
        String(month).padStart(2, "0");

      const label =
        this.monthFormatter.format(date);

      this.months.push({
        value,
        label
      });
    }
  }

  getActiveMonthKey() {
    const selected =
      this.monthInput?.value || "";

    const valid = this.months.some(
      month => month.value === selected
    );

    return valid
      ? selected
      : this.months[0]?.value || "";
  }

  // ========================================
  // MATRIX API
  // ========================================

  async fetchEvents(monthKey) {
    if (this.monthCache.has(monthKey)) {
      return this.monthCache.get(monthKey);
    }

    if (this.pendingRequests.has(monthKey)) {
      return this.pendingRequests.get(monthKey);
    }

    const request = this.requestMonth(monthKey);

    this.pendingRequests.set(
      monthKey,
      request
    );

    try {
      const events = await request;

      if (!this.destroyed) {
        this.monthCache.set(
          monthKey,
          events
        );
      }

      return events;
    } finally {
      this.pendingRequests.delete(monthKey);
    }
  }

  buildApiUrl(endpoint, startDate, finishDate) {
    const url = new URL(
      endpoint,
      window.location.origin
    );

    url.searchParams.set(
      "startDate",
      String(startDate)
    );

    url.searchParams.set(
      "finishDate",
      String(finishDate)
    );

    return url.toString();
  }

  // ----------------------------------------
  // API RESPONSE VALIDATION
  // ----------------------------------------

  async fetchMatrixData(url) {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json"
      },
      signal: this.abortController.signal
    });

    if (!response.ok) {
      throw new Error(
        `Matrix request failed: ${response.status}`
      );
    }

    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw new Error(
        "Matrix returned an invalid JSON response."
      );
    }

    if (
      !data ||
      typeof data !== "object" ||
      Array.isArray(data)
    ) {
      throw new Error(
        "Invalid Matrix API response."
      );
    }

    const count = Number(data.count);

    const hasZeroCount =
      data.count !== null &&
      data.count !== undefined &&
      data.count !== "" &&
      Number.isFinite(count) &&
      count === 0;

    const hasEmptyMessage =
      typeof data.message === "string" &&
      /no events|0 events/i.test(
        data.message
      );

    const isEmptyResponse =
      hasZeroCount || hasEmptyMessage;

    if (data.status !== "success") {
      if (isEmptyResponse) {
        return {
          ...data,
          events: {}
        };
      }

      throw new Error(
        data.message ||
        "Matrix API request failed."
      );
    }

    if (
      data.events === null ||
      data.events === undefined
    ) {
      if (isEmptyResponse) {
        return {
          ...data,
          events: {}
        };
      }

      throw new Error(
        "Matrix API response is missing events."
      );
    }

    if (
      typeof data.events !== "object"
    ) {
      throw new Error(
        "Matrix API events have an unexpected format."
      );
    }

    return data;
  }

  // ----------------------------------------
  // REQUEST MONTH
  // ----------------------------------------

  async requestMonth(monthKey) {
    const { startDate, finishDate } =
      this.getMonthRange(monthKey);

    const cleanUrl = this.buildApiUrl(
      this.apiUrl,
      startDate,
      finishDate
    );

    const availabilityUrl = this.buildApiUrl(
      this.availabilityApiUrl,
      startDate,
      finishDate
    );

    const [cleanData, availabilityResult] =
      await Promise.all([
        this.fetchMatrixData(cleanUrl),

        this.fetchMatrixData(availabilityUrl)
          .then(data => ({
            data,
            error: null
          }))
          .catch(error => ({
            data: null,
            error
          }))
      ]);

    if (
      availabilityResult.error?.name ===
      "AbortError"
    ) {
      throw availabilityResult.error;
    }

    if (availabilityResult.error) {
      console.warn(
        `Availability unavailable for ${monthKey}:`,
        availabilityResult.error
      );
    }

    const availabilityById = new Map();

    if (availabilityResult.data) {
      const rawAvailability =
        Array.isArray(
          availabilityResult.data.events
        )
          ? availabilityResult.data.events
          : Object.values(
              availabilityResult.data.events
            );

      rawAvailability.forEach(item => {
        if (
          item &&
          item.venuedateid !== null &&
          item.venuedateid !== undefined
        ) {
          availabilityById.set(
            String(item.venuedateid),
            item
          );
        }
      });
    }

    const rawEvents =
      Array.isArray(cleanData.events)
        ? cleanData.events
        : Object.values(cleanData.events);

    const events = rawEvents
      .map(rawEvent => {
        if (
          !rawEvent ||
          typeof rawEvent !== "object"
        ) {
          return null;
        }

        const eventId = String(
          rawEvent.venuedateid ??
          rawEvent.id ??
          ""
        );

        const availability =
          availabilityById.get(eventId);

        return this.normaliseEvent({
          ...rawEvent,
          percentageremaining:
            availability?.percentageremaining ??
            null,
          percentageused:
            availability?.percentageused ??
            null
        });
      })
      .filter(Boolean)
      .sort((a, b) =>
        a.timestamp - b.timestamp ||
        (a.startTime ?? 0) -
          (b.startTime ?? 0)
      );

    return events;
  }

  // ========================================
  // EVENT NORMALISATION
  // ========================================

  
  normaliseEvent(raw) {
    const timestamp = Number(raw.date);

    if (
      !Number.isFinite(timestamp) ||
      timestamp <= 0
    ) {
      return null;
    }

    const date = new Date(timestamp * 1000);
    const uk = this.getUKParts(date);

    const venue = String(
      raw.venuedescription || ""
    ).trim();

    const venueDescription = String(
      raw.venuedescription || ""
    ).trim();

    const title = String(
      raw.title ||
      raw.eventtitle ||
      raw.eventname ||
      ""
    ).trim();

    const town = String(
      raw.venuetown || ""
    ).trim();

    const county = String(
      raw.venuecounty || ""
    ).trim();

    const location = String(
      raw.venuelocation ||
      [town, county]
        .filter(Boolean)
        .join(", ")
    ).trim();

    const rawStartTime =
      raw.start_time ??
      raw.venueearlieststart;

    const parsedStartTime =
      rawStartTime === null ||
      rawStartTime === undefined ||
      rawStartTime === ""
        ? null
        : Number(rawStartTime);

    const startTime =
      Number.isFinite(parsedStartTime) &&
      parsedStartTime >= 0
        ? parsedStartTime
        : null;

    const monthKey =
      `${uk.year}-` +
      String(uk.month).padStart(2, "0");

    const monthLabel =
      this.monthFormatter.format(
        new Date(
          Date.UTC(
            uk.year,
            uk.month - 1,
            1
          )
        )
      );

    const parsePercentage = value => {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        return null;
      }

      const number = Number(value);

      return (
        Number.isFinite(number) &&
        number >= 0 &&
        number <= 100
      )
        ? number
        : null;
    };

    return {
      id: String(
        raw.venuedateid ||
        raw.id ||
        `${timestamp}-${venue}`
      ),

      timestamp,
      date,

      year: uk.year,
      month: uk.month,
      day: uk.day,

      monthKey,
      monthLabel,

      venue,
      venueDescription,
      title,
      location,
      startTime,

      percentageremaining:
        parsePercentage(
          raw.percentageremaining
        ),

      percentageused:
        parsePercentage(
          raw.percentageused
        ),

      image:
        typeof raw.image === "string"
          ? raw.image
          : "",

      url:
        typeof raw.venuelink === "string"
          ? raw.venuelink
          : "",

      searchText: [
        venue,
        title,
        location
      ].join(" ").toLowerCase()
    };
  }


  // ========================================
  // AVAILABILITY
  // ========================================

  getAvailabilityStatus(percentage) {
    if (
      percentage === null ||
      percentage === undefined ||
      percentage === ""
    ) {
      return null;
    }

    const remaining = Number(percentage);

    if (
      !Number.isFinite(remaining) ||
      remaining < 0 ||
      remaining > 100
    ) {
      return null;
    }

    if (remaining === 0) {
      return {
        label: "Confirm availability",
        className: "confirm"
      };
    }

    if (remaining <= 30) {
      return {
        label: "Limited availability",
        className: "limited"
      };
    }

    return {
      label: "Available",
      className: "available"
    };
  }

  isUpcomingEvent(event, now = this.getUKParts()) {
    const today =
      now.year * 10000 +
      now.month * 100 +
      now.day;

    const eventDay =
      event.year * 10000 +
      event.month * 100 +
      event.day;

    if (eventDay > today) return true;
    if (eventDay < today) return false;

    if (event.startTime === null) {
      return true;
    }

    const currentSeconds =
      now.hour * 3600 +
      now.minute * 60 +
      now.second;

    return event.startTime >= currentSeconds;
  }

  // ========================================
  // PROGRESSIVE LOADING
  // ========================================

  async loadCalendar() {
    const requestId = ++this.requestId;
    const monthKey = this.getActiveMonthKey();

    if (!monthKey || this.destroyed) {
      return;
    }

    this.setLoading(true);

    this.monthsContainer?.replaceChildren();

    if (this.emptyElement) {
      this.emptyElement.hidden = true;
    }

    try {
      const events =
        await this.fetchEvents(monthKey);

      if (
        this.destroyed ||
        requestId !== this.requestId
      ) {
        return;
      }

      this.events = events;
      this.applyFilters();

      void this.prefetchNextMonths(
        monthKey
      );
    } catch (error) {
      if (
        error.name !== "AbortError" &&
        requestId === this.requestId &&
        !this.destroyed
      ) {
        this.showError(error.message);
      }
    } finally {
      if (
        requestId === this.requestId &&
        !this.destroyed
      ) {
        this.setLoading(false);
      }
    }
  }

  async prefetchNextMonths(monthKey) {
    const prefetchId = ++this.prefetchId;

    const index = this.months.findIndex(
      month => month.value === monthKey
    );

    if (index === -1) return;

    const nextMonths = this.months.slice(
      index + 1,
      index + 1 + this.prefetchCount
    );

    for (const month of nextMonths) {
      if (
        this.destroyed ||
        prefetchId !== this.prefetchId
      ) {
        return;
      }

      try {
        await this.fetchEvents(
          month.value
        );
      } catch (error) {
        if (
          error.name === "AbortError"
        ) {
          return;
        }

        console.warn(
          `Prefetch failed for ${month.label}:`,
          error
        );
      }
    }
  }

  // ========================================
  // SEARCH AND FILTERS
  // ========================================

  getFilters() {
    return {
      search:
        this.searchInput?.value
          .trim()
          .toLowerCase() || "",

      month:
        this.monthInput?.value || ""
    };
  }

  onSearch() {
    clearTimeout(this.searchTimer);

    this.searchTimer = setTimeout(() => {
      if (!this.destroyed) {
        this.applyFilters();
      }
    }, this.searchDelay);
  }

  applyFilters() {
    const filters = this.getFilters();

    // Calculate UK time once per filtering operation.
    const now = this.getUKParts();

    this.filteredEvents =
      this.events.filter(event => {
        const matchesSearch =
          !filters.search ||
          event.searchText.includes(
            filters.search
          );

        const matchesMonth =
          !filters.month ||
          event.monthKey ===
            filters.month;

        return (
          matchesSearch &&
          matchesMonth &&
          this.isUpcomingEvent(
            event,
            now
          )
        );
      });

    this.render();
    this.updateResultsCount();
    this.updateUrl(filters);
  }

  async onMonthChange(monthKey) {
    const valid =
      !monthKey ||
      this.months.some(
        month => month.value === monthKey
      );

    if (!valid) return;

    clearTimeout(this.searchTimer);

    if (this.monthInput) {
      this.monthInput.value = monthKey;
    }

    this.updateMonthLabel();
    this.closeMonthDropdown();

    await this.loadCalendar();
  }

  async clearFilters() {
    clearTimeout(this.searchTimer);

    if (this.searchInput) {
      this.searchInput.value = "";
    }

    if (this.monthInput) {
      this.monthInput.value =
        this.getCurrentMonthKey();
    }

    this.updateMonthLabel();
    this.closeMonthDropdown();

    await this.loadCalendar();
  }

  // ========================================
  // MONTH DROPDOWN
  // ========================================

  renderMonthOptions() {
    if (!this.monthMenu) return;

    const fragment =
      document.createDocumentFragment();

    this.months.forEach(month => {
      let option = null;

      if (this.monthOptionTemplate) {
        const clone =
          this.monthOptionTemplate.content.cloneNode(
            true
          );

        option = clone.querySelector(
          "[data-event-month-option]"
        );
      }

      if (!option) {
        option = document.createElement(
          "button"
        );

        option.type = "button";
        option.setAttribute(
          "role",
          "option"
        );
      }

      option.textContent = month.label;
      option.dataset.monthValue =
        month.value;

      fragment.appendChild(option);
    });

    this.monthMenu.replaceChildren(
      fragment
    );

    this.updateMonthSelection();
  }

  updateMonthSelection() {
    if (!this.monthMenu) return;

    const selected =
      this.getActiveMonthKey();

    this.monthMenu
      .querySelectorAll(
        "[data-month-value]"
      )
      .forEach(option => {
        const isSelected =
          option.dataset.monthValue ===
          selected;

        option.setAttribute(
          "aria-selected",
          String(isSelected)
        );

        option.classList.toggle(
          "is-selected",
          isSelected
        );
      });
  }

  updateMonthLabel() {
    const selected =
      this.getActiveMonthKey();

    const month = this.months.find(
      item => item.value === selected
    );

    if (this.monthLabel) {
      this.monthLabel.textContent =
        month?.label ||
        this.months[0]?.label ||
        "";
    }

    this.updateMonthSelection();
  }

  toggleMonthDropdown() {
    if (!this.monthMenu) return;

    const isOpening =
      this.monthMenu.hidden;

    this.monthMenu.hidden =
      !isOpening;

    this.monthDropdown?.classList.toggle(
      "is-open",
      isOpening
    );

    this.monthTrigger?.setAttribute(
      "aria-expanded",
      String(isOpening)
    );
  }

  closeMonthDropdown() {
    if (!this.monthMenu) return;

    this.monthMenu.hidden = true;

    this.monthDropdown?.classList.remove(
      "is-open"
    );

    this.monthTrigger?.setAttribute(
      "aria-expanded",
      "false"
    );
  }

  onOutsideClick(event) {
    if (
      this.monthDropdown &&
      !this.monthDropdown.contains(
        event.target
      )
    ) {
      this.closeMonthDropdown();
    }
  }

  onMonthMenuClick(event) {
    const option = event.target.closest(
      "[data-month-value]"
    );

    if (!option) return;

    void this.onMonthChange(
      option.dataset.monthValue
    );
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeMonthDropdown();
    }
  }

  // ========================================
  // RENDER
  // ========================================

  render() {
    if (
      !this.monthsContainer ||
      !this.monthTemplate ||
      !this.cardTemplate
    ) {
      console.warn(
        "Calendar Liquid templates are missing."
      );

      return;
    }

    const grouped = new Map();

    this.filteredEvents.forEach(event => {
      if (!grouped.has(event.monthKey)) {
        grouped.set(event.monthKey, {
          value: event.monthKey,
          label: event.monthLabel,
          events: []
        });
      }

      grouped
        .get(event.monthKey)
        .events.push(event);
    });

    const fragment =
      document.createDocumentFragment();

    [...grouped.values()]
      .sort((a, b) =>
        a.value.localeCompare(
          b.value
        )
      )
      .forEach(month => {
        fragment.appendChild(
          this.renderMonth(month)
        );
      });

    this.monthsContainer.replaceChildren(
      fragment
    );

    this.renderEmptyState();
  }

  // ========================================
  // EMPTY STATE
  // ========================================

  renderEmptyState() {
    if (!this.emptyElement) return;

    const isEmpty =
      this.filteredEvents.length === 0;

    this.emptyElement.hidden =
      !isEmpty;

    if (!isEmpty) return;

    const monthKey =
      this.getActiveMonthKey();

    const month = this.months.find(
      item => item.value === monthKey
    );

    const monthLabel =
      month?.label || "this month";

    const heading =
      this.emptyElement.querySelector(
        "[data-calendar-empty-heading]"
      );

    const description =
      this.emptyElement.querySelector(
        "[data-calendar-empty-text]"
      );

    const hasSearch = Boolean(
      this.searchInput?.value.trim()
    );

    if (heading) {
      heading.textContent = hasSearch
        ? this.calendarContainer?.dataset
            .emptyHeading ||
          "No matching events"
        : `No events scheduled for ${monthLabel}`;
    }

    if (description) {
      description.textContent = hasSearch
        ? this.calendarContainer?.dataset
            .emptyText ||
          "Try changing or clearing your search."
        : this.emptyElement.dataset
            .noEventsText ||
          "There are currently no driving experiences scheduled for this month. Please explore another month to find upcoming events.";
    }
  }

  // ========================================
  // MONTH RENDERING
  // ========================================

  renderMonth(month) {
    const fragment =
      this.monthTemplate.content.cloneNode(
        true
      );

    const section = fragment.querySelector(
      "[data-events-calendar-month]"
    );

    if (section) {
      section.dataset.calendarMonth =
        month.value;

      section.dataset.calendarMonthLabel =
        month.label;
    }

    const eyebrow = fragment.querySelector(
      "[data-month-eyebrow]"
    );

    if (eyebrow) {
      const label =
        this.calendarContainer?.dataset
          .upcomingLabel || "";

      eyebrow.textContent = label;
      eyebrow.hidden = !label;
    }

    const heading = fragment.querySelector(
      "[data-month-heading]"
    );

    if (heading) {
      heading.textContent =
        month.label;
    }

    const grid = fragment.querySelector(
      "[data-event-grid]"
    );

    month.events.forEach(event => {
      grid?.appendChild(
        this.renderEventCard(event)
      );
    });

    return fragment;
  }

  // ========================================
  // EVENT CARD RENDERING
  // ========================================

  renderEventCard(event) {
    const fragment =
      this.cardTemplate.content.cloneNode(
        true
      );

    const setText = (selector, value) => {
      const element =
        fragment.querySelector(selector);

      if (element) {
        element.textContent = value;
      }
    };

    const weekday =
      this.weekdayFormatter.format(
        event.date
      );

    setText(
      "[data-event-day]",
      String(event.day)
    );

    setText(
      "[data-event-weekday]",
      weekday
    );

    setText(
      "[data-event-venue]",
      event.venue
    );

    setText(
      "[data-event-location]",
      event.location
    );

    const dateElement =
      fragment.querySelector(
        "[data-event-date]"
      );

    if (dateElement) {
      dateElement.setAttribute(
        "datetime",
        `${event.year}-` +
        `${String(event.month).padStart(2, "0")}-` +
        String(event.day).padStart(
          2,
          "0"
        )
      );
    }

    // ----------------------------------------
    // START TIME
    // ----------------------------------------

    const timeElement =
      fragment.querySelector(
        "[data-event-time]"
      );

    if (timeElement) {
      const hasTime =
        event.startTime !== null;

      timeElement.textContent = hasTime
        ? this.formatStartTime(
            event.startTime
          )
        : "";

      const timeWrapper =
        timeElement.closest(
          ".event-card__time"
        );

      if (timeWrapper) {
        timeWrapper.hidden =
          !hasTime;
      }
    }

    // ----------------------------------------
    // AVAILABILITY
    // ----------------------------------------

    const availabilityElement =
      fragment.querySelector(
        "[data-event-availability]"
      );

    if (availabilityElement) {
      const availability =
        this.getAvailabilityStatus(
          event.percentageremaining
        );

      availabilityElement.classList.remove(
        "event-card__availability-status--available",
        "event-card__availability-status--limited",
        "event-card__availability-status--confirm"
      );

      if (availability) {
        availabilityElement.textContent =
          availability.label;

        availabilityElement.classList.add(
          `event-card__availability-status--${availability.className}`
        );

        availabilityElement.hidden =
          false;
      } else {
        availabilityElement.textContent =
          "";

        availabilityElement.hidden =
          true;
      }
    }

    // ----------------------------------------
    // LOCATION
    // ----------------------------------------

    const locationElement =
      fragment.querySelector(
        "[data-event-location]"
      );

    if (locationElement) {
      locationElement.hidden =
        !event.location;
    }

    // ----------------------------------------
    // IMAGE OPTIMISATION
    // ----------------------------------------

    const media =
      fragment.querySelector(
        "[data-event-media]"
      );

    const image =
      fragment.querySelector(
        "[data-event-image]"
      );

    if (image && event.image) {
      image.loading = "lazy";
      image.decoding = "async";

      image.alt = event.venue;
      image.src = event.image;
    } else if (media) {
      media.hidden = true;
    }

    
    // ----------------------------------------
    // EVENT BUTTON
    // Navigate to available driving experiences
    // ----------------------------------------

    
// ----------------------------------------
// EVENT BUTTON
// Navigate to available driving experiences
// ----------------------------------------


const button = fragment.querySelector(
  "[data-event-button]"
);

const showButton =
  this.calendarContainer?.dataset.showEventButton === "true";

const venueDateId = String(event.id || "").trim();

if (
  button &&
  showButton &&
  /^\d+$/.test(venueDateId)
) {
  const url = new URL(
    "/pages/available-driving-experiences",
    window.location.origin
  );

  // Matrix event ID
  url.searchParams.set("venueDateId", venueDateId);

  // Venue name (also used to identify Junior events)
  if (event.venue) {
    url.searchParams.set("venue", event.venue);
  }

  // Event date
  const eventDate = [
    event.year,
    String(event.month).padStart(2, "0"),
    String(event.day).padStart(2, "0")
  ].join("-");

  url.searchParams.set("date", eventDate);

  button.setAttribute(
    "href",
    url.pathname + url.search
  );

  button.hidden = false;

  setText(
    "[data-event-button-label]",
    this.calendarContainer?.dataset.viewLabel ||
      "View Experiences"
  );
} else if (button) {
  button.hidden = true;
}

    return fragment;
  }

  formatStartTime(seconds) {
    const hours = Math.floor(
      seconds / 3600
    );

    const minutes = Math.floor(
      (seconds % 3600) / 60
    );

    return (
      String(hours).padStart(2, "0") +
      ":" +
      String(minutes).padStart(2, "0")
    );
  }

  // ========================================
  // RESULTS COUNT
  // ========================================

  updateResultsCount() {
    if (!this.resultsElement) {
      return;
    }

    const count =
      this.filteredEvents.length;

    this.resultsElement.textContent =
      `${count} ` +
      (count === 1
        ? "event"
        : "events");
  }

  // ========================================
  // URL PARAMETERS
  // ========================================

  updateUrl(filters) {
    const url = new URL(
      window.location.href
    );

    if (filters.month) {
      url.searchParams.set(
        "month",
        filters.month
      );
    } else {
      url.searchParams.delete(
        "month"
      );
    }

    if (filters.search) {
      url.searchParams.set(
        "search",
        filters.search
      );
    } else {
      url.searchParams.delete(
        "search"
      );
    }

    window.history.replaceState(
      {},
      "",
      url.toString()
    );
  }

  // ========================================
  // LOADING AND ERRORS
  // ========================================

  setLoading(loading) {
    if (this.loadingElement) {
      this.loadingElement.hidden =
        !loading;
    }
  }

  showError(message) {
    this.setLoading(false);

    if (this.resultsElement) {
      this.resultsElement.textContent =
        message;
    }

    this.monthsContainer?.replaceChildren();

    if (this.emptyElement) {
      this.emptyElement.hidden = true;
    }

    console.error(
      "Events calendar:",
      message
    );
  }

  // ========================================
  // INITIALISATION
  // ========================================

  async init() {
    this.buildMonths();
    this.renderMonthOptions();

    const params =
      new URLSearchParams(
        window.location.search
      );

    const requestedMonth =
      params.get("month") || "";

    if (
      this.monthInput &&
      this.months.some(
        month =>
          month.value === requestedMonth
      )
    ) {
      this.monthInput.value =
        requestedMonth;
    }

    if (
      this.searchInput &&
      params.has("search")
    ) {
      this.searchInput.value =
        params.get("search");
    }

    this.updateMonthLabel();

    this.searchInput?.addEventListener(
      "input",
      this.handleSearch
    );

    this.clearButton?.addEventListener(
      "click",
      this.handleClear
    );

    this.monthTrigger?.addEventListener(
      "click",
      this.handleMonthTrigger
    );

    this.monthMenu?.addEventListener(
      "click",
      this.handleMonthMenuClick
    );

    document.addEventListener(
      "click",
      this.handleOutsideClick
    );

    document.addEventListener(
      "keydown",
      this.handleKeydown
    );

    if (this.destroyed) return;

    await this.loadCalendar();
  }

  // ========================================
  // CLEANUP
  // ========================================

  destroy() {
    this.destroyed = true;

    this.requestId++;
    this.prefetchId++;

    clearTimeout(this.searchTimer);

    this.abortController.abort();

    this.monthCache.clear();
    this.pendingRequests.clear();

    this.searchInput?.removeEventListener(
      "input",
      this.handleSearch
    );

    this.clearButton?.removeEventListener(
      "click",
      this.handleClear
    );

    this.monthTrigger?.removeEventListener(
      "click",
      this.handleMonthTrigger
    );

    this.monthMenu?.removeEventListener(
      "click",
      this.handleMonthMenuClick
    );

    document.removeEventListener(
      "click",
      this.handleOutsideClick
    );

    document.removeEventListener(
      "keydown",
      this.handleKeydown
    );
  }
}

// ========================================
// SHOPIFY INITIALISATION
// ========================================

let globalEventsCalendar = null;

function initialiseGlobalEventsCalendar() {
  const toolbar = document.querySelector(
    "[data-events-calendar-global]"
  );

  globalEventsCalendar?.destroy();
  globalEventsCalendar = null;

  if (!toolbar) return;

  globalEventsCalendar =
    new GlobalEventsCalendar(toolbar);
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    initialiseGlobalEventsCalendar
  );
} else {
  initialiseGlobalEventsCalendar();
}

document.addEventListener(
  "shopify:section:load",
  initialiseGlobalEventsCalendar
);

document.addEventListener(
  "shopify:section:unload",
  () => {
    globalEventsCalendar?.destroy();
    globalEventsCalendar = null;
  }
);

document.addEventListener(
  "shopify:section:reorder",
  initialiseGlobalEventsCalendar
);
